import CompanyPreviewPanelContent from "@/components/company/company-preview-panel-content";
import CompanyTagPreview from "@/components/company/company-tag-preview";
import {
  companyLinkOpeningStore,
  useCompanyLinkOpening,
} from "@/stores/company-link-opening-store";
import {
  companyPreviewPanelStore,
  useCompanyPreviewPanelEntry,
} from "@/stores/company-preview-panel-store";
import {
  Sheet,
  SheetContent,
  SheetDragHandle,
  SheetTitle,
  useScrollAwareSheetDrag,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { useCloseOnHistoryBack } from "@/hooks/useCloseOnHistoryBack";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  type LucideIcon,
} from "lucide-react";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

type CompanyPreviewPanelNavButtonProps = {
  icon: LucideIcon;
  label: string;
  disabled: boolean;
  onClick: () => void;
};

function CompanyPreviewPanelNavButton({
  icon: Icon,
  label,
  disabled,
  onClick,
}: CompanyPreviewPanelNavButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
          className="w-12 h-12 compact:size-10 rounded-full flex items-center justify-center cursor-pointer border-none outline-none bg-gray-200 dark:bg-gray-700 text-black dark:text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 ease-in-out disabled:opacity-50 disabled:pointer-events-none disabled:hover:scale-100"
        >
          <Icon className="size-5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="left">{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Singleton company preview panel, rendered as a Shadcn Sheet. Mount it once in
 * the authenticated layout: any trigger (table cell, chat mention, ...) opens it
 * through `companyPreviewPanelStore`, and it survives trigger remounts.
 */
export function CompanyPreviewPanel() {
  const { t } = useTranslation();
  const { isMobile } = useDeviceFormat();
  const {
    state,
    company,
    tag,
    isVisible,
    hasPrevious,
    hasNext,
    hasMultipleStops,
  } = useCompanyPreviewPanelEntry();
  // On a phone the panel is one thing wherever it opens from — a conversation,
  // the welcome screen, a list: a sheet over the screen with no scrim or focus
  // trap, the screen kept live above it.
  const isModal = !isMobile && (state ? state.modal : true);
  // An "inline" surface is rendered by its host container (the docked
  // company-page chat) in the chat's own bounds; the singleton sheet stands
  // down so the two don't both show.
  const isInline = state?.surface === "inline";
  const { pathname } = useLocation();
  // A company link tapped on a phone: the panel rises at once, by the link's
  // name, while the company is read — a tap is never dead on a slow network.
  const opening = useCompanyLinkOpening();
  const isOpening = isMobile && opening !== null && !isVisible;
  const isShown = (!isInline && isVisible) || isOpening;
  const close = () => {
    companyLinkOpeningStore.cancel();
    companyPreviewPanelStore.close();
  };

  // The panel previews something on the page the user is looking at: moving to
  // another page or tab (every tab is its own route) dismisses it, whichever
  // surface it is shown on. Query-string changes (pagination, filters) keep it.
  useEffect(() => {
    companyLinkOpeningStore.cancel();
    if (companyPreviewPanelStore.getSnapshot()) {
      companyPreviewPanelStore.close();
    }
  }, [pathname]);

  // A bottom sheet on a phone: the back gesture closes it, not the page under
  // it.
  // From the opening state to the company, the sheet stays open: one entry.
  useCloseOnHistoryBack(isMobile && isShown, close);

  const dragRef = useScrollAwareSheetDrag({ onClose: close });

  return (
    <Sheet
      open={isShown}
      modal={isModal}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      {isShown && (
        <SheetContent
          // On a phone a company is read in a sheet rising from the bottom, as
          // the Claude app shows its documents: the conversation stays in view
          // above it, and a pull down puts it away.
          side={isMobile ? "bottom" : "right"}
          overlay={isModal}
          // Frost the page behind the modal preview so the panel reads as a
          // focused overlay rather than a bare sheet abutting the content.
          overlayClassName="backdrop-blur-sm"
          // Non-modal panels (the chat) must not steal clicks from the page
          // behind them: keep the panel open on outside interaction so it
          // closes only via the close button or Escape.
          onInteractOutside={(event) => {
            if (!isModal) event.preventDefault();
          }}
          className={cn(
            "p-0 gap-0 flex flex-col data-[state=open]:duration-[400ms] data-[state=closed]:duration-[250ms]",
            isMobile
              ? "h-[92dvh] rounded-t-3xl border-0 shadow-2xl pb-[env(safe-area-inset-bottom,0px)]"
              : "w-full sm:max-w-(--company-panel-width) shadow-none border-l border-[#e2e8f0] dark:border-[#1e293b]",
            // Non-modal panels (the chat) sit below the fixed header instead of
            // overlapping it, so the panel stays within the conversation area.
            !isModal &&
              !isMobile &&
              "top-[calc(var(--app-header-height)+1px)] h-auto",
          )}
        >
          <SheetTitle className="sr-only">
            {company?.name ?? tag?.name ?? opening?.label}
          </SheetTitle>
          {isMobile && <SheetDragHandle onClose={close} />}
          {/* Chevrons only make sense when several companies are in the panel
              (chat conversations); a single-company preview has nowhere to
              step, so the nav is hidden rather than shown permanently disabled. */}
          {/* Beside a side sheet only: next to a bottom sheet, full width on a
              phone, they would sit off-screen. */}
          {hasMultipleStops && !isMobile && (
            <div className="hidden sm:flex absolute top-1/2 -translate-y-1/2 -left-16 z-10 flex-col gap-3">
              <CompanyPreviewPanelNavButton
                icon={ChevronUpIcon}
                label={t(
                  "components.dataTableCompanies.columns.sidePanelPrevious",
                )}
                disabled={!hasPrevious}
                onClick={() => companyPreviewPanelStore.previous()}
              />
              <CompanyPreviewPanelNavButton
                icon={ChevronDownIcon}
                label={t("components.dataTableCompanies.columns.sidePanelNext")}
                disabled={!hasNext}
                onClick={() => companyPreviewPanelStore.next()}
              />
            </div>
          )}
          {/* As every bottom sheet: a pull down from the content, once it is
              scrolled to its top, puts the panel away — not the handle alone. */}
          <div ref={isMobile ? dragRef : undefined} className="flex-1 min-h-0">
            {isOpening && opening ? (
              <CompanyPanelOpening label={opening.label} />
            ) : company ? (
              <CompanyPreviewPanelContent company={company} />
            ) : (
              tag && <CompanyTagPreview tag={tag} />
            )}
          </div>
        </SheetContent>
      )}
    </Sheet>
  );
}

/** The panel while a tapped company is read: its name, and its shape. */
function CompanyPanelOpening({ label }: { readonly label: string }) {
  return (
    <div className="flex flex-col gap-4 px-5 pt-2" aria-busy>
      <div className="flex gap-3 items-center">
        <Skeleton className="rounded-xl size-12 shrink-0" />
        <p className="text-lg font-semibold truncate">{label}</p>
      </div>
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-24 rounded-xl" />
      <Skeleton className="h-24 rounded-xl" />
    </div>
  );
}
